import { filterClasses } from "@swivl-mono/helpers";
import Mention from "@tiptap/extension-mention";
import Placeholder from "@tiptap/extension-placeholder";
import { Fragment, Node as PMNode, Schema } from "@tiptap/pm/model";
import { TextSelection } from "@tiptap/pm/state";
import { EditorContent, ReactRenderer, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { SuggestionProps } from "@tiptap/suggestion";
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";

import { ErrorText, FormFieldWidth, HelperText, Label, getFormFieldWidthClass } from "../FormField";
import { EditPillDropdown } from "./EditPillDropdown";
import { EditingPillExtension, editingPillKey } from "./EditingPillExtension";
import "./VariableInput.scss";
import { VariableSuggestionList, VariableSuggestionListHandle, VariableSuggestionListProps } from "./VariableSuggestionList";
import { valueToHtml } from "./valueToHtml";

function positionPopup(popup: HTMLElement, clientRect?: (() => DOMRect | null) | null): void {
  const rect = clientRect?.();
  if (!rect) return;
  const parent = popup.parentElement;
  if (!parent || parent === document.body) {
    popup.style.position = "fixed";
    popup.style.top = `${rect.bottom + 4}px`;
    popup.style.left = `${rect.left}px`;
    return;
  }
  // The popup is mounted inside a Radix Dialog so it stays within
  // react-remove-scroll's shard (and thus stays scrollable). The dialog uses
  // a CSS transform for centering, which makes position:fixed descendants
  // relative to the dialog rather than the viewport — so we offset against
  // the dialog's visual rect with position:absolute.
  const parentRect = parent.getBoundingClientRect();
  popup.style.position = "absolute";
  popup.style.top = `${rect.bottom - parentRect.top + 4}px`;
  popup.style.left = `${rect.left - parentRect.left}px`;
}

export interface VariableInputTag {
  id: string;
  display: string;
}

export enum VariableInputType {
  Normal = "normal",
  SingleLine = "singleLine",
  Bubble = "bubble",
  NoBorder = "noBorder"
}

const TYPE_CLASS: Record<VariableInputType, string> = {
  [VariableInputType.Normal]: "variableInputNormal",
  [VariableInputType.SingleLine]: "variableInputSingleLine",
  [VariableInputType.Bubble]: "variableInputBubble",
  [VariableInputType.NoBorder]: "variableInputNoBorder"
};

export interface VariableInputProps {
  value?: string;
  variables: VariableInputTag[];
  displayTransform?: (id: string, display: string) => string;
  type?: VariableInputType;
  name?: string;
  disabled?: boolean;
  readOnly?: boolean;
  placeholder?: string;
  id?: string;
  label?: string;
  helperText?: string;
  error?: string;
  required?: boolean;
  autoFocus?: boolean;
  className?: string;
  containerClassName?: string;
  width?: FormFieldWidth;
  maxLength?: number;
  onMaxLengthExceeded?(): void;
  onLengthChange?(length: number): void;
  onChange?(value: string, name?: string): void;
  onReturnPressed?(): void;
  onBlur?(): void;
  onFocus?(): void;
}

export function VariableInput(props: VariableInputProps) {
  const {
    value = "",
    variables,
    displayTransform,
    type = VariableInputType.Normal,
    name,
    disabled,
    readOnly,
    placeholder,
    id,
    label,
    helperText,
    error,
    required,
    autoFocus,
    className: classNameProp,
    containerClassName,
    width = FormFieldWidth.FULL,
    maxLength,
    onMaxLengthExceeded,
    onLengthChange,
    onChange,
    onReturnPressed,
    onBlur,
    onFocus
  } = props;

  const maxLengthRef = useRef(maxLength);
  const onMaxLengthExceededRef = useRef(onMaxLengthExceeded);
  const onLengthChangeRef = useRef(onLengthChange);
  useLayoutEffect(() => {
    maxLengthRef.current = maxLength;
    onMaxLengthExceededRef.current = onMaxLengthExceeded;
    onLengthChangeRef.current = onLengthChange;
  }, [maxLength, onMaxLengthExceeded, onLengthChange]);

  const generatedId = useId();
  const inputId = id || generatedId;
  const containerWidthClass = getFormFieldWidthClass(width);
  const resolvedContainerClassName = filterClasses(containerWidthClass, containerClassName);

  const variablesRef = useRef(variables);
  const displayTransformRef = useRef(displayTransform);
  const variablesMapRef = useRef<Map<string, VariableInputTag>>(new Map(variables.map((v) => [v.id, v])));

  useLayoutEffect(() => {
    variablesRef.current = variables;
    variablesMapRef.current = new Map(variables.map((v) => [v.id, v]));
  }, [variables]);

  useLayoutEffect(() => {
    displayTransformRef.current = displayTransform;
  }, [displayTransform]);

  const [editingPill, setEditingPill] = useState<{ from: number; to: number; rect: DOMRect } | null>(null);
  const setEditingPillRef = useRef(setEditingPill);
  useLayoutEffect(() => {
    setEditingPillRef.current = setEditingPill;
  }, [setEditingPill]);

  const onReturnPressedRef = useRef(onReturnPressed);
  useLayoutEffect(() => {
    onReturnPressedRef.current = onReturnPressed;
  }, [onReturnPressed]);

  const displayFor = (id: string): string => {
    const found = variablesMapRef.current.get(id);
    const label = found?.display ?? id;
    return displayTransformRef.current ? displayTransformRef.current(id, label) : label;
  };

  const visibleLength = (content: PMNode | Fragment): number => {
    let len = 0;
    content.descendants((node) => {
      if (node.isText) len += node.text?.length ?? 0;
      else if (node.type.name === "mention") len += displayFor(node.attrs.id).length;
      else if (node.type.name === "hardBreak") len += 1;
      return true;
    });
    return len;
  };

  const takeInlineUpTo = (content: Fragment, limit: number, schema: Schema): { fragment: Fragment; truncated: boolean } => {
    const out: PMNode[] = [];
    let used = 0;
    let truncated = false;
    content.descendants((node) => {
      if (truncated) return false;
      if (node.isText) {
        const text = node.text ?? "";
        const room = limit - used;
        if (text.length <= room) {
          out.push(schema.text(text));
          used += text.length;
        } else {
          if (room > 0) out.push(schema.text(text.slice(0, room)));
          truncated = true;
        }
        return false;
      }
      if (node.type.name === "mention" || node.type.name === "hardBreak") {
        const len = node.type.name === "mention" ? displayFor(node.attrs.id).length : 1;
        if (used + len > limit) {
          truncated = true;
          return false;
        }
        out.push(node);
        used += len;
        return false;
      }
      return true;
    });
    return { fragment: Fragment.from(out), truncated };
  };

  const editor = useEditor({
    editable: !disabled && !readOnly,
    content: valueToHtml(value, displayFor),
    extensions: [
      StarterKit.configure({
        heading: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
        listKeymap: false,
        blockquote: false,
        codeBlock: false,
        horizontalRule: false,
        code: false,
        bold: false,
        italic: false,
        strike: false,
        underline: false,
        link: false,
        trailingNode: false,
        hardBreak: type === VariableInputType.SingleLine ? false : undefined
      }),
      Placeholder.configure({ placeholder: placeholder ?? "" }),
      EditingPillExtension,
      Mention.extend({
        renderText({ node }) {
          return `#*${node.attrs.id}*#`;
        },
        renderHTML({ node, HTMLAttributes }) {
          return [
            "span",
            {
              ...HTMLAttributes,
              class: "VariableInput__pill",
              "data-type": "mention",
              "data-id": node.attrs.id
            },
            displayFor(node.attrs.id)
          ];
        }
      }).configure({
        HTMLAttributes: { class: "VariableInput__pill" },
        deleteTriggerWithBackspace: true,
        suggestion: {
          char: "#",
          decorationTag: "span",
          decorationClass: "VariableInput__pill VariableInput__pill--pending",
          items: ({ query }) => {
            const q = query.toLowerCase();
            if (!q) {
              return variablesRef.current.slice(0, 100);
            }
            const rank = (v: VariableInputTag): number => {
              const id = v.id.toLowerCase();
              const display = v.display.toLowerCase();
              const idTail = id.includes(":") ? id.slice(id.indexOf(":") + 1) : id;
              if (id.startsWith(q) || idTail.startsWith(q)) return 0;
              if (display.startsWith(q)) return 1;
              if (display.includes(q)) return 2;
              if (id.includes(q)) return 3;
              return 4;
            };
            return variablesRef.current
              .filter((v) => v.display.toLowerCase().includes(q) || v.id.toLowerCase().includes(q))
              .sort((a, b) => rank(a) - rank(b))
              .slice(0, 100);
          },
          command: ({ editor: ed, range, props: selected }) => {
            const tag = selected as VariableInputTag;
            ed.chain()
              .focus()
              .insertContentAt(range, [
                { type: "mention", attrs: { id: tag.id, label: tag.display } },
                { type: "text", text: " " }
              ])
              .run();
          },
          render: () => {
            let component: ReactRenderer<VariableSuggestionListHandle, VariableSuggestionListProps> | null = null;
            let popup: HTMLDivElement | null = null;
            return {
              onStart: (p: SuggestionProps<VariableInputTag>) => {
                component = new ReactRenderer(VariableSuggestionList, {
                  props: { items: p.items, onSelect: p.command },
                  editor: p.editor
                });
                popup = document.createElement("div");
                popup.className = "VariableInput__suggestionPortal";
                // Mount inside the closest Radix Dialog so the popup is part
                // of react-remove-scroll's shard (otherwise wheel events get
                // cancelled and the suggestion list isn't scrollable). Falls
                // back to body when there's no dialog.
                const dialogHost = p.editor?.view?.dom?.closest('[role="dialog"]') as HTMLElement | null;
                (dialogHost ?? document.body).appendChild(popup);
                popup.appendChild(component.element);
                positionPopup(popup, p.clientRect);
              },
              onUpdate: (p: SuggestionProps<VariableInputTag>) => {
                component?.updateProps({ items: p.items, onSelect: p.command });
                if (popup) positionPopup(popup, p.clientRect);
              },
              onKeyDown: ({ event }: { event: KeyboardEvent }) => {
                if (event.key === "Escape") {
                  popup?.remove();
                  popup = null;
                  return true;
                }
                return component?.ref?.onKeyDown(event) ?? false;
              },
              onExit: () => {
                component?.destroy();
                component = null;
                popup?.remove();
                popup = null;
              }
            };
          }
        }
      })
    ],
    editorProps: {
      handleKeyDown(view, event) {
        if (event.key === "Enter" && (type === VariableInputType.SingleLine || onReturnPressedRef.current)) {
          const suggestionActive = view.dom.querySelector(".VariableInput__pill--pending") !== null;
          if (suggestionActive) return false;
          onReturnPressedRef.current?.();
          return true;
        }
        return false;
      },
      handleTextInput(view, from, to, text) {
        const max = maxLengthRef.current;
        if (max === undefined) return false;
        const newDoc = view.state.tr.insertText(text, from, to).doc;
        if (visibleLength(newDoc) <= max) return false;
        onMaxLengthExceededRef.current?.();
        return true;
      },
      handlePaste(view, _event, slice) {
        const max = maxLengthRef.current;
        if (max === undefined) return false;
        const { from, to } = view.state.selection;
        const remaining = max - visibleLength(view.state.tr.delete(from, to).doc);
        if (remaining <= 0) {
          onMaxLengthExceededRef.current?.();
          return true;
        }
        const { fragment, truncated } = takeInlineUpTo(slice.content, remaining, view.state.schema);
        if (truncated) onMaxLengthExceededRef.current?.();
        if (fragment.size === 0) return true;
        view.dispatch(view.state.tr.replaceWith(from, to, fragment));
        return true;
      },
      handleClickOn(view, _pos, node, nodePos, _event, direct) {
        if (!direct) return false;
        if (!view.editable) return false;
        if (node.type.name === "mention") {
          const from = nodePos;
          const to = nodePos + node.nodeSize;
          const tr = view.state.tr.setMeta(editingPillKey, { from, to }).setSelection(TextSelection.create(view.state.doc, to));
          view.dispatch(tr);
          view.focus();
          const dom = view.nodeDOM(nodePos) as HTMLElement | null;
          const rect = dom?.getBoundingClientRect();
          if (rect) {
            setEditingPillRef.current({ from, to, rect });
          }
          return true;
        }
        return false;
      }
    },
    onUpdate: ({ editor: ed }) => {
      setEditingPillRef.current(null);
      onLengthChangeRef.current?.(visibleLength(ed.state.doc));
      if (!onChange) return;
      const serialized = ed.getText({ blockSeparator: "\n" });
      onChange(serialized, name);
    },
    onBlur: () => onBlur?.(),
    onFocus: () => onFocus?.()
  });

  useEffect(() => {
    if (!editor) return;
    const current = editor.getText({ blockSeparator: "\n" });
    if (current !== (value ?? "")) {
      editor.commands.setContent(valueToHtml(value ?? "", displayFor), { emitUpdate: false });
    }
    onLengthChangeRef.current?.(visibleLength(editor.state.doc));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  useEffect(() => {
    if (editor) editor.setEditable(!disabled && !readOnly);
  }, [editor, disabled, readOnly]);

  useEffect(() => {
    if (!autoFocus || !editor || disabled || readOnly) return;
    editor.commands.focus("end");
  }, [editor]);

  const closeEditing = useCallback((): void => {
    setEditingPill(null);
    if (editor) editor.view.dispatch(editor.view.state.tr.setMeta(editingPillKey, null));
  }, [editor]);

  useEffect(() => {
    if (!editingPill) return;
    const handleOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest(".VariableInput__editPortal") || target.closest(".VariableInput__pill")) return;
      closeEditing();
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [editingPill, closeEditing]);

  const handleReplacePill = (tag: VariableInputTag): void => {
    if (!editor || !editingPill) return;
    editor
      .chain()
      .focus()
      .insertContentAt({ from: editingPill.from, to: editingPill.to }, { type: "mention", attrs: { id: tag.id, label: tag.display } })
      .run();
    closeEditing();
  };

  const className = filterClasses(
    "VariableInputWrapperLocal",
    TYPE_CLASS[type],
    disabled && "disabled",
    readOnly && "readOnly",
    error && "error",
    classNameProp
  );

  const wrapperRef = useRef<HTMLDivElement>(null);
  const dialogHost = (wrapperRef.current?.closest('[role="dialog"]') as HTMLElement | null) ?? null;

  return (
    <div className={resolvedContainerClassName}>
      {label && (
        <Label htmlFor={inputId} required={required} className="mb-1">
          {label}
        </Label>
      )}
      <div
        ref={wrapperRef}
        id={inputId}
        className={className}
        onMouseDown={(e) => {
          if (!editor || disabled || readOnly) return;
          if (e.target === e.currentTarget) {
            e.preventDefault();
            editor.commands.focus("end");
          }
        }}
      >
        <EditorContent editor={editor} />
        {editingPill && (
          <EditPillDropdown
            anchorRect={editingPill.rect}
            variables={variables}
            hostElement={dialogHost}
            onSelect={handleReplacePill}
            onClose={closeEditing}
          />
        )}
      </div>
      {error && <ErrorText>{error}</ErrorText>}
      {!error && helperText && <HelperText>{helperText}</HelperText>}
    </div>
  );
}
